import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  RefreshCw,
  AlertCircle,
  ShieldOff,
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
  Building2,
  Link as LinkIcon,
  Users,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { useAuth } from '@/context/auth-context';

interface Organization {
  id: string;
  name: string;
  slug: string;
  tax_id: string | null;
  billing_address: string | null;
  billing_email: string | null;
  country_code: string | null;
  created_at: string;
  updated_at: string;
  site_count: number;
  spreadsheet_id: string | null;
  spreadsheet_url: string | null;
  report_status: string | null;
  last_synced_at: string | null;
}

interface SiteRow {
  id: string;
  name: string;
  url: string;
  domain?: string | null;
  status?: string | null;
}

// Mirror of the backend's 409 assignment_mismatch shape (organizations.js).
// assign_missing — requested assign IDs that didn't exist in the sites table.
// unassign_mismatch — requested unassign IDs that weren't currently in this org.
interface AssignErrorDetail {
  message: string;
  assign_missing?: string[];
  unassign_mismatch?: string[];
}

interface OrgMember {
  user_id: string;
  email: string;
  name: string | null;
  granted_at: string;
  granted_by_email: string | null;
  // Effective access (PR #2311 + #2297 follow-up): a user is only
  // truly active in the org when BOTH the per-row uop.admin_approved
  // AND the global users.admin_approved are true. The endpoint
  // surfaces both raw fields plus a derived effective so the UI
  // can render the discrepancy without recomputing.
  //
  // OPTIONAL because a legacy backend (pre-PR #2311 deploy) doesn't
  // emit these fields. The deriveMemberEffective() helper below
  // handles all three states: known true, known false, unknown
  // (legacy → treat as active for backwards-compat).
  admin_approved?: boolean;
  user_admin_approved?: boolean;
  effective_admin_approved?: boolean;
}

// Helper extracted to ./orgMemberEffective so the Vitest spec can
// import it without pulling the full Organizations component tree
// (and its auth-fetch + window globals).
import { deriveMemberEffective, type EffectiveState } from './orgMemberEffective';

interface OrgFormState {
  name: string;
  slug: string;
  tax_id: string;
  billing_address: string;
  billing_email: string;
  country_code: string;
}

const EMPTY_FORM: OrgFormState = {
  name: '',
  slug: '',
  tax_id: '',
  billing_address: '',
  billing_email: '',
  country_code: '',
};

const SLUG_RE = /^[a-z0-9-]+$/;

// Slug is lowercase + digits + hyphen only (DB constraint). Auto-derive a
// candidate from the name so super-admins don't have to retype it; they can
// still override before submit.
function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function orgToForm(org: Organization): OrgFormState {
  return {
    name: org.name ?? '',
    slug: org.slug ?? '',
    tax_id: org.tax_id ?? '',
    billing_address: org.billing_address ?? '',
    billing_email: org.billing_email ?? '',
    country_code: org.country_code ?? '',
  };
}

// Convert empty form fields → null on the wire so the backend doesn't store
// '' for optional columns. Name and slug stay as strings (required).
function formToPayload(form: OrgFormState): Record<string, string | null> {
  const out: Record<string, string | null> = {
    name: form.name.trim(),
    slug: form.slug.trim(),
  };
  for (const key of ['tax_id', 'billing_address', 'billing_email', 'country_code'] as const) {
    const v = form[key].trim();
    out[key] = v === '' ? null : v;
  }
  return out;
}

export default function Organizations() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create / edit dialog state
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<OrgFormState>(EMPTY_FORM);
  const [slugTouched, setSlugTouched] = useState(false);
  const [savePending, setSavePending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  // Codex PR #2217 iter-3 MINOR-2: dialog-local delete error so the 409
  // has_sites reason is visible inside the confirmation dialog, not hidden
  // behind it in the page-level error alert.
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Manage Sites dialog state
  const [managingOrg, setManagingOrg] = useState<Organization | null>(null);
  const [orgSites, setOrgSites] = useState<SiteRow[]>([]);
  const [allSites, setAllSites] = useState<SiteRow[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [pendingAssign, setPendingAssign] = useState<Set<string>>(new Set());
  const [pendingUnassign, setPendingUnassign] = useState<Set<string>>(new Set());
  const [assignPending, setAssignPending] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  // Codex PR #2217 iter-3 MINOR-1: dialog-local error for the Manage Sites
  // modal. A 409 assignment_mismatch ships assign_missing[] and
  // unassign_mismatch[] arrays — render them inline so the operator can see
  // which specific site IDs didn't apply rather than just "save did nothing".
  const [assignError, setAssignError] = useState<AssignErrorDetail | null>(null);

  // Org members dialog state
  const [membersOrg, setMembersOrg] = useState<Organization | null>(null);
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [allUsers, setAllUsers] = useState<{ id: string; email: string; name: string | null; role: string; admin_approved: boolean }[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberRevokePending, setMemberRevokePending] = useState<string | null>(null);
  const [memberAddPending, setMemberAddPending] = useState<string | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);

  const fetchOrgs = useCallback(async (isRefresh: boolean) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await authFetch('/api/organizations');
      if (!response.ok) {
        const body = await response.text().catch(() => '');
        throw new Error(`GET /api/organizations ${response.status} — ${body || response.statusText}`);
      }
      const data: Organization[] = await response.json();
      if (!Array.isArray(data)) throw new Error('GET /api/organizations returned non-array body');
      setOrgs(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgs(false);
  }, [fetchOrgs]);

  // ── Create / edit ──

  const openCreate = () => {
    setEditingOrg(null);
    setForm(EMPTY_FORM);
    setSlugTouched(false);
    setFormError(null);
    setCreating(true);
  };

  const openEdit = (org: Organization) => {
    setEditingOrg(org);
    setForm(orgToForm(org));
    // Edit start: the form is pre-filled, so respect whatever slug is in DB.
    // Setting touched=true prevents the name-driven auto-slug from clobbering
    // the existing slug if the user retypes the name.
    setSlugTouched(true);
    setFormError(null);
    setCreating(false);
  };

  const closeForm = () => {
    setEditingOrg(null);
    setCreating(false);
    setForm(EMPTY_FORM);
    setSlugTouched(false);
    setFormError(null);
  };

  const onNameChange = (value: string) => {
    setForm((f) => {
      const next: OrgFormState = { ...f, name: value };
      if (!slugTouched) next.slug = slugify(value);
      return next;
    });
  };

  const onSlugChange = (value: string) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: value }));
  };

  const submitForm = async () => {
    setFormError(null);
    const payload = formToPayload(form);
    if (!payload.name) {
      setFormError('Name is required');
      return;
    }
    if (!payload.slug || !SLUG_RE.test(payload.slug)) {
      setFormError('Slug must match ^[a-z0-9-]+$ (lowercase letters, digits, hyphens)');
      return;
    }

    setSavePending(true);
    try {
      const isEdit = !!editingOrg;
      const url = isEdit ? `/api/organizations/${editingOrg!.id}` : '/api/organizations';
      const method = isEdit ? 'PUT' : 'POST';
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const msg = body?.message || body?.error || response.statusText;
        throw new Error(`${method} ${url} ${response.status} — ${msg}`);
      }
      await fetchOrgs(true);
      closeForm();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavePending(false);
    }
  };

  // ── Delete ──

  const confirmDelete = async () => {
    if (!deletingOrg) return;
    setDeletePending(true);
    setDeleteError(null);
    try {
      const response = await authFetch(`/api/organizations/${deletingOrg.id}`, { method: 'DELETE' });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        const msg = body?.message || body?.error || response.statusText;
        throw new Error(`DELETE ${response.status} — ${msg}`);
      }
      setDeletingOrg(null);
      await fetchOrgs(true);
    } catch (err) {
      // Codex PR #2217 iter-3 MINOR-2: surface inside the dialog so the 409
      // has_sites reason is visible while the operator decides what to do.
      setDeleteError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeletePending(false);
    }
  };

  // ── Manage Sites ──

  const openManageSites = async (org: Organization) => {
    setManagingOrg(org);
    setPendingAssign(new Set());
    setPendingUnassign(new Set());
    setSiteSearch('');
    setAssignError(null);
    setSitesLoading(true);
    try {
      const [assignedRes, allRes] = await Promise.all([
        authFetch(`/api/organizations/${org.id}/sites`),
        authFetch('/api/sites'),
      ]);
      if (!assignedRes.ok) throw new Error(`GET /api/organizations/${org.id}/sites ${assignedRes.status}`);
      if (!allRes.ok) throw new Error(`GET /api/sites ${allRes.status}`);
      const assigned: SiteRow[] = await assignedRes.json();
      const all: SiteRow[] = await allRes.json();
      setOrgSites(Array.isArray(assigned) ? assigned : []);
      setAllSites(Array.isArray(all) ? all : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setManagingOrg(null);
    } finally {
      setSitesLoading(false);
    }
  };

  const closeManageSites = () => {
    setManagingOrg(null);
    setOrgSites([]);
    setAllSites([]);
    setPendingAssign(new Set());
    setPendingUnassign(new Set());
    setSiteSearch('');
    setAssignError(null);
  };

  // The "effective" set of currently-in-org sites takes pending changes into
  // account so the UI reflects the saved state, not the loaded state.
  const effectiveOrgSiteIds = useMemo(() => {
    const set = new Set(orgSites.map((s) => s.id));
    for (const id of pendingUnassign) set.delete(id);
    for (const id of pendingAssign) set.add(id);
    return set;
  }, [orgSites, pendingAssign, pendingUnassign]);

  const togglePendingAssign = (siteId: string) => {
    setPendingAssign((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
    // If a previously assigned site was queued for unassign, toggling here
    // shouldn't leave it in both buckets. We pick the side the user clicked.
    setPendingUnassign((prev) => {
      const next = new Set(prev);
      next.delete(siteId);
      return next;
    });
  };

  const togglePendingUnassign = (siteId: string) => {
    setPendingUnassign((prev) => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
    setPendingAssign((prev) => {
      const next = new Set(prev);
      next.delete(siteId);
      return next;
    });
  };

  const saveAssignments = async () => {
    if (!managingOrg) return;
    const assign = Array.from(pendingAssign);
    const unassign = Array.from(pendingUnassign);
    if (assign.length === 0 && unassign.length === 0) {
      closeManageSites();
      return;
    }
    setAssignPending(true);
    setAssignError(null);
    try {
      const response = await authFetch(`/api/organizations/${managingOrg.id}/sites/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assign, unassign }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        // Codex PR #2217 iter-3 MINOR-1: keep the dialog open and surface
        // the 409 assignment_mismatch detail (assign_missing /
        // unassign_mismatch arrays) inline. The transaction rolled back
        // server-side, so the queued state is still a valid retry target
        // once the operator removes the offending IDs.
        const msg = body?.message || body?.error || response.statusText;
        setAssignError({
          message: `POST sites/assign ${response.status} — ${msg}`,
          assign_missing: Array.isArray(body?.assign_missing) ? body.assign_missing : undefined,
          unassign_mismatch: Array.isArray(body?.unassign_mismatch) ? body.unassign_mismatch : undefined,
        });
        return;
      }
      await fetchOrgs(true);
      closeManageSites();
    } catch (err) {
      // Network / parse failure — also stays in the dialog.
      setAssignError({ message: err instanceof Error ? err.message : String(err) });
    } finally {
      setAssignPending(false);
    }
  };

  // Sites available for assignment: those not currently effective-in-org.
  // We filter by the search box (case-insensitive name/url) and cap the
  // displayed list to keep large catalogues responsive.
  const candidateSites = useMemo(() => {
    const search = siteSearch.trim().toLowerCase();
    return allSites
      .filter((s) => !effectiveOrgSiteIds.has(s.id))
      .filter((s) => {
        if (!search) return true;
        return (
          (s.name || '').toLowerCase().includes(search) ||
          (s.url || '').toLowerCase().includes(search)
        );
      })
      .slice(0, 50);
  }, [allSites, effectiveOrgSiteIds, siteSearch]);

  // Sites currently in the org (effective view): original list minus pending
  // unassigns plus pending assigns that originated outside.
  const currentlyInOrg = useMemo(() => {
    const all = new Map<string, SiteRow>();
    for (const s of orgSites) all.set(s.id, s);
    for (const s of allSites) {
      if (effectiveOrgSiteIds.has(s.id) && !all.has(s.id)) all.set(s.id, s);
    }
    return Array.from(all.values())
      .filter((s) => effectiveOrgSiteIds.has(s.id))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [orgSites, allSites, effectiveOrgSiteIds]);

  // ── Org Members ──

  const openMembers = async (org: Organization) => {
    setMembersOrg(org);
    setMemberSearch('');
    setMembersError(null);
    setMembersLoading(true);
    try {
      const [membersRes, usersRes] = await Promise.all([
        authFetch(`/api/organizations/${org.id}/permissions`),
        authFetch('/api/users'),
      ]);
      if (!membersRes.ok) throw new Error(`GET permissions ${membersRes.status}`);
      if (!usersRes.ok) throw new Error(`GET users ${usersRes.status}`);
      const members: OrgMember[] = await membersRes.json();
      const usersBody = await usersRes.json();
      const users = Array.isArray(usersBody) ? usersBody : (usersBody.users ?? []);
      setOrgMembers(Array.isArray(members) ? members : []);
      setAllUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : String(err));
    } finally {
      setMembersLoading(false);
    }
  };

  const closeMembers = () => {
    setMembersOrg(null);
    setOrgMembers([]);
    setAllUsers([]);
    setMemberSearch('');
    setMembersError(null);
    setMemberRevokePending(null);
    setMemberAddPending(null);
  };

  const revokeMember = async (orgId: string, userId: string) => {
    setMemberRevokePending(userId);
    setMembersError(null);
    try {
      const res = await authFetch(`/api/organizations/${orgId}/permissions/${userId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || body?.error || `DELETE ${res.status}`);
      }
      setOrgMembers((prev) => prev.filter((m) => m.user_id !== userId));
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : String(err));
    } finally {
      setMemberRevokePending(null);
    }
  };

  const addMember = async (orgId: string, userId: string) => {
    setMemberAddPending(userId);
    setMembersError(null);
    try {
      const res = await authFetch(`/api/organizations/${orgId}/permissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || body?.error || `POST ${res.status}`);
      }
      // Reload members list so granted_at + granted_by_email are fresh from DB.
      // Non-OK reload is an error — silent skip would show a stale list with no
      // indication that the reload failed (forbidden silent-fallback pattern).
      const membersRes = await authFetch(`/api/organizations/${orgId}/permissions`);
      if (!membersRes.ok) {
        throw new Error(`Member added but list refresh failed (${membersRes.status}) — close and reopen to see current members`);
      }
      const members: OrgMember[] = await membersRes.json();
      setOrgMembers(Array.isArray(members) ? members : []);
      setMemberSearch('');
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : String(err));
    } finally {
      setMemberAddPending(null);
    }
  };

  const memberCandidates = useMemo(() => {
    const existingIds = new Set(orgMembers.map((m) => m.user_id));
    const search = memberSearch.trim().toLowerCase();
    return allUsers
      .filter((u) => !existingIds.has(u.id))
      .filter((u) => u.admin_approved)
      .filter((u) => {
        if (!search) return true;
        return (u.email || '').toLowerCase().includes(search) || (u.name || '').toLowerCase().includes(search);
      })
      .slice(0, 30);
  }, [allUsers, orgMembers, memberSearch]);

  // ── Render ──

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-7 w-7" />
            Organizations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage organizations and assign sites. Each site belongs to exactly one organization;
            super-admins control assignments.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchOrgs(true)}
            disabled={refreshing || loading}
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="ml-2">Refresh</span>
          </Button>
          {isSuperAdmin && (
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" />
              New Organization
            </Button>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Organizations error</AlertTitle>
          <AlertDescription className="break-all">{error}</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          Loading organizations…
        </div>
      )}

      {!loading && orgs.length === 0 && !error && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No organizations yet.
            {isSuperAdmin && ' Click "New Organization" to create one.'}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {orgs.map((org) => (
          <Card key={org.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle className="flex items-center gap-2 flex-wrap">
                    <span className="truncate">{org.name}</span>
                    <Badge variant="secondary">
                      {org.site_count} site{org.site_count === 1 ? '' : 's'}
                    </Badge>
                  </CardTitle>
                  <CardDescription className="font-mono text-xs mt-1 break-all">
                    {org.slug}
                  </CardDescription>
                </div>
                <div className="flex gap-1 shrink-0">
                  {isSuperAdmin && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => openMembers(org)} title="Manage members">
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(org)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingOrg(org)}
                        title={org.site_count > 0 ? 'Reassign sites first to enable delete' : 'Delete organization'}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-3 gap-x-3 gap-y-1 text-xs mb-4">
                {org.tax_id && (
                  <>
                    <dt className="text-muted-foreground">Tax ID</dt>
                    <dd className="col-span-2 break-all">{org.tax_id}</dd>
                  </>
                )}
                {org.billing_email && (
                  <>
                    <dt className="text-muted-foreground">Billing email</dt>
                    <dd className="col-span-2 break-all">{org.billing_email}</dd>
                  </>
                )}
                {org.country_code && (
                  <>
                    <dt className="text-muted-foreground">Country</dt>
                    <dd className="col-span-2">{org.country_code}</dd>
                  </>
                )}
              </dl>
              <div className="flex flex-wrap gap-2">
                {isSuperAdmin && (
                  <Button variant="outline" size="sm" onClick={() => openManageSites(org)}>
                    <LinkIcon className="h-4 w-4 mr-1" />
                    Manage Sites
                  </Button>
                )}
                {org.spreadsheet_url ? (
                  <Button variant="outline" size="sm" asChild>
                    <a href={org.spreadsheet_url} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open Sheet
                    </a>
                  </Button>
                ) : (
                  <Badge variant="outline" className="text-muted-foreground">
                    No sheet report yet
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create / Edit dialog */}
      <Dialog
        open={creating || editingOrg !== null}
        onOpenChange={(open) => {
          if (!open && !savePending) closeForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingOrg ? `Edit ${editingOrg.name}` : 'New Organization'}</DialogTitle>
            <DialogDescription>
              Name and slug are required. Slug must be lowercase letters, digits, and hyphens; it is the
              stable identifier across reports and integrations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="org-name">Name *</Label>
              <Input
                id="org-name"
                value={form.name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="TradeInn S.L."
                disabled={savePending}
              />
            </div>
            <div>
              <Label htmlFor="org-slug">Slug *</Label>
              <Input
                id="org-slug"
                value={form.slug}
                onChange={(e) => onSlugChange(e.target.value)}
                placeholder="tradeinn"
                disabled={savePending}
                className="font-mono"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="org-tax">Tax ID</Label>
                <Input
                  id="org-tax"
                  value={form.tax_id}
                  onChange={(e) => setForm((f) => ({ ...f, tax_id: e.target.value }))}
                  placeholder="B12345678"
                  disabled={savePending}
                />
              </div>
              <div>
                <Label htmlFor="org-country">Country code</Label>
                <Input
                  id="org-country"
                  value={form.country_code}
                  onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value }))}
                  placeholder="ES"
                  disabled={savePending}
                  maxLength={10}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="org-email">Billing email</Label>
              <Input
                id="org-email"
                value={form.billing_email}
                onChange={(e) => setForm((f) => ({ ...f, billing_email: e.target.value }))}
                placeholder="billing@tradeinn.com"
                disabled={savePending}
              />
            </div>
            <div>
              <Label htmlFor="org-addr">Billing address</Label>
              <Input
                id="org-addr"
                value={form.billing_address}
                onChange={(e) => setForm((f) => ({ ...f, billing_address: e.target.value }))}
                placeholder="Street, ZIP, City, Country"
                disabled={savePending}
              />
            </div>
            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="break-all">{formError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm} disabled={savePending}>
              Cancel
            </Button>
            <Button onClick={submitForm} disabled={savePending}>
              {savePending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingOrg ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deletingOrg !== null}
        onOpenChange={(open) => {
          if (!open && !deletePending) {
            setDeletingOrg(null);
            setDeleteError(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deletingOrg?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deletingOrg && deletingOrg.site_count > 0 ? (
                <>
                  This organization still has <strong>{deletingOrg.site_count}</strong> site
                  {deletingOrg.site_count === 1 ? '' : 's'} assigned. Reassign them first; the
                  backend will refuse the delete otherwise.
                </>
              ) : (
                <>
                  This permanently removes the organization, all user permission rows, and the
                  Google Sheet report record. The Sheet file in Drive is not deleted.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Delete failed</AlertTitle>
              <AlertDescription className="break-all">{deleteError}</AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePending}>Cancel</AlertDialogCancel>
            {/*
              Codex PR #2217 iter-4 BLOCKING: Radix AlertDialogAction calls
              onOpenChange(false) synchronously on click, BEFORE confirmDelete
              resolves. A 409 has_sites sets deleteError after the dialog has
              already unmounted (setDeletingOrg(null) ran during close) so the
              error was never rendered — recreating the exact silent-failure
              the iter-3 fix was meant to address. Plain Button has no
              default-close side effect; confirmDelete closes the dialog
              itself on success (setDeletingOrg(null) inside the try block)
              and leaves it open on failure so deleteError can render inline.
              preventDefault() is a belt-and-braces guard against any future
              wrapper around Button adding a synchronous-close behavior.
            */}
            <Button
              variant="destructive"
              disabled={deletePending}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              {deletePending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage sites */}
      <Dialog
        open={managingOrg !== null}
        onOpenChange={(open) => {
          if (!open && !assignPending) closeManageSites();
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage sites — {managingOrg?.name}</DialogTitle>
            <DialogDescription>
              Each site belongs to exactly one organization. Assigning a site here will move it from
              its current organization. Unassigning sets <code>organization_id</code> to NULL.
            </DialogDescription>
          </DialogHeader>
          {sitesLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading sites…
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold mb-2">In this organization ({currentlyInOrg.length})</h3>
                {currentlyInOrg.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No sites assigned yet.</p>
                ) : (
                  <ul className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                    {currentlyInOrg.map((s) => {
                      const queuedAssign = pendingAssign.has(s.id);
                      const queuedUnassign = pendingUnassign.has(s.id);
                      return (
                        <li
                          key={s.id}
                          className="flex items-center justify-between gap-2 text-sm py-1"
                        >
                          <div className="min-w-0">
                            <div className="font-medium truncate">{s.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{s.url}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {queuedAssign && <Badge variant="outline">queued add</Badge>}
                            {queuedUnassign && <Badge variant="destructive">queued remove</Badge>}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                queuedAssign
                                  ? togglePendingAssign(s.id)
                                  : togglePendingUnassign(s.id)
                              }
                            >
                              {queuedAssign || queuedUnassign ? 'Undo' : 'Remove'}
                            </Button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Add sites</h3>
                <Input
                  placeholder="Search by name or URL"
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  className="mb-2"
                />
                {candidateSites.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No matching sites.</p>
                ) : (
                  <ul className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                    {candidateSites.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-2 text-sm py-1"
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">{s.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{s.url}</div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => togglePendingAssign(s.id)}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
                {allSites.length > 50 && siteSearch === '' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Showing first 50 sites — refine the search to see more.
                  </p>
                )}
              </div>

              {assignError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Assignment failed</AlertTitle>
                  <AlertDescription className="space-y-2">
                    <div className="break-all">{assignError.message}</div>
                    {assignError.assign_missing && assignError.assign_missing.length > 0 && (
                      <div>
                        <div className="font-semibold">Assign missing (site ID not found):</div>
                        <ul className="font-mono text-xs list-disc pl-5">
                          {assignError.assign_missing.map((id) => (
                            <li key={`am-${id}`} className="break-all">{id}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {assignError.unassign_mismatch && assignError.unassign_mismatch.length > 0 && (
                      <div>
                        <div className="font-semibold">
                          Unassign mismatch (not currently in this organization):
                        </div>
                        <ul className="font-mono text-xs list-disc pl-5">
                          {assignError.unassign_mismatch.map((id) => (
                            <li key={`um-${id}`} className="break-all">{id}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      No changes were applied — the server rolled back the whole batch. Remove the
                      offending IDs from the queue and save again.
                    </div>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeManageSites} disabled={assignPending}>
              Cancel
            </Button>
            <Button
              onClick={saveAssignments}
              disabled={
                assignPending ||
                sitesLoading ||
                (pendingAssign.size === 0 && pendingUnassign.size === 0)
              }
            >
              {assignPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save ({pendingAssign.size + pendingUnassign.size} change
              {pendingAssign.size + pendingUnassign.size === 1 ? '' : 's'})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/*
        Read-only notice for non-super-admin users who reached this URL via a
        direct link AND have at least one approved user_organization_permissions
        row (the backend list filters by uop, so an empty orgs[] for these
        users means no access — we render the same "No organizations yet"
        empty-state above and skip this banner). The sidebar does not link
        here for them — by design, since there is no formal "org_content_manager"
        role, access is permission-table only.
      */}
      {/* Org Members dialog */}
      <Dialog
        open={membersOrg !== null}
        onOpenChange={(open) => {
          if (!open && !memberRevokePending && !memberAddPending) closeMembers();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Members — {membersOrg?.name}</DialogTitle>
            <DialogDescription>
              Members get viewer access to the organization's Google Sheet report. Access syncs on
              the next hourly run.
            </DialogDescription>
          </DialogHeader>
          {membersLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading…
            </div>
          ) : (
            <div className="space-y-4">
              {membersError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="break-all">{membersError}</AlertDescription>
                </Alert>
              )}

              <div>
                {(() => {
                  // Per-row effective state. 'unknown' rows (legacy
                  // backend, codex iter-1 BLOCKING) count as active
                  // so we don't regress an un-deployed cluster's UI
                  // to "0 active + N deactivated".
                  const states = orgMembers.map(deriveMemberEffective);
                  const deactivatedCount = states.filter((s) => s === 'inactive').length;
                  const activeCount = orgMembers.length - deactivatedCount;
                  return (
                    <h3 className="text-sm font-semibold mb-2">
                      Current members ({activeCount}
                      {deactivatedCount > 0 && (
                        <>
                          {' active'}
                          <span className="text-muted-foreground font-normal">
                            {' '}+ {deactivatedCount} deactivated
                          </span>
                        </>
                      )}
                      )
                    </h3>
                  );
                })()}
                {orgMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No members yet.</p>
                ) : (
                  <ul className="space-y-1 max-h-48 overflow-y-auto border rounded p-2">
                    {orgMembers.map((m) => {
                      const eff = deriveMemberEffective(m);
                      // Only render the badge when we KNOW the row
                      // is inactive. 'unknown' (legacy backend) gets
                      // no badge — matches the pre-#2311 display so
                      // the rolling deploy never shows a confusing
                      // "Deactivated" badge on every row.
                      const showDeactivatedBadge = eff === 'inactive';
                      const badgeTooltip = m.user_admin_approved === false
                        ? 'User account is globally deactivated — access is denied even though this org row is approved.'
                        : m.admin_approved === false
                          ? 'Org membership row is not admin_approved — access is denied.'
                          : 'Effective access is denied for this row.';
                      return (
                        <li key={m.user_id} className="flex items-center justify-between gap-2 text-sm py-1">
                          <div className="min-w-0">
                            <div className="font-medium truncate flex items-center gap-1.5">
                              <span className="truncate">{m.email}</span>
                              {showDeactivatedBadge && (
                                <Badge
                                  variant="outline"
                                  className="text-[10px] uppercase tracking-wide text-amber-700 border-amber-300 bg-amber-50"
                                  title={badgeTooltip}
                                >
                                  Deactivated
                                </Badge>
                              )}
                            </div>
                            {m.name && (
                              <div className="text-xs text-muted-foreground truncate">{m.name}</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => membersOrg && void revokeMember(membersOrg.id, m.user_id)}
                            disabled={memberRevokePending === m.user_id}
                          >
                            {memberRevokePending === m.user_id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Trash2 className="h-3 w-3" />
                            )}
                          </Button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-2">Add member</h3>
                <Input
                  placeholder="Search by email or name"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  className="mb-2"
                />
                {memberCandidates.length === 0 && memberSearch.trim() !== '' ? (
                  <p className="text-sm text-muted-foreground">No matching users.</p>
                ) : memberCandidates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Type to search content managers.
                  </p>
                ) : (
                  <ul className="space-y-1 max-h-40 overflow-y-auto border rounded p-2">
                    {memberCandidates.map((u) => (
                      <li key={u.id} className="flex items-center justify-between gap-2 text-sm py-1">
                        <div className="min-w-0">
                          <div className="font-medium truncate">{u.email}</div>
                          {u.name && (
                            <div className="text-xs text-muted-foreground truncate">{u.name}</div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => membersOrg && void addMember(membersOrg.id, u.id)}
                          disabled={memberAddPending === u.id}
                        >
                          {memberAddPending === u.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : (
                            <Plus className="h-3 w-3 mr-1" />
                          )}
                          Add
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeMembers} disabled={!!memberRevokePending || !!memberAddPending}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!isSuperAdmin && !loading && orgs.length > 0 && (
        <Alert>
          <ShieldOff className="h-4 w-4" />
          <AlertTitle>View-only</AlertTitle>
          <AlertDescription>
            Creating, editing, deleting, and site assignment require a super-admin role.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
